import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Download,
  FileText,
  Layers,
  Link2,
  Play,
  Video,
  BookOpen,
  Building2,
  GraduationCap,
  Search,
  Megaphone,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/contexts/AuthContext'
import { useCourse, useCourseModules } from '@/hooks/useCourses'
import { CourseModuleModel, CourseResourceModel } from '@/models/course.model'
import { paths } from '@/routes/path'
import { programmesService } from '@/services/programmesService'
import { departmentsService } from '@/services/departmentsService'
import { UserRole } from '@/models/user.model'
import { useCourseAnnouncements } from '@/hooks/useAnnouncements'
import { AnnouncementModel } from '@/models/announcement.model'

function formatDate(date?: string | null) {
  if (!date) return 'Not set'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.valueOf())) return 'Not set'
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getResourceIcon(resource: CourseResourceModel) {
  const type = resource.type?.toLowerCase() ?? ''
  if (type.includes('video')) return <Video className="h-4 w-4" />
  if (type.includes('pdf') || type.includes('doc') || type.includes('document')) return <FileText className="h-4 w-4" />
  if (type.includes('link')) return <Link2 className="h-4 w-4" />
  return <BookOpen className="h-4 w-4" />
}

function formatFileSize(bytes?: number) {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

function ModuleCard({
  module,
  courseId,
  isHighlighted,
}: {
  module: CourseModuleModel
  courseId: string
  isHighlighted?: boolean
}) {
  const resources = module.resources

  return (
    <Card
      id={`module-${module.id}`}
      className={`border-0 shadow-md bg-white/80 backdrop-blur-sm transition ${
        isHighlighted ? 'ring-2 ring-primary-200' : ''
      }`}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
        <div className="space-y-2">
        <Badge variant="outline" className="bg-primary-50 text-primary-600 border-primary-100">
            Module {module.order ?? '—'}
          </Badge>
          <CardTitle className="text-xl text-secondary-900">{module.title}</CardTitle>
          {module.description && (
            <CardDescription className="text-secondary-600 max-w-2xl">
              {module.description}
            </CardDescription>
          )}
        </div>
        <Link to={`${paths.app.courses.content.build(courseId)}?moduleId=${module.id}`}>
          <Button className="bg-primary-600 hover:bg-primary-700 text-white">
            <Play className="mr-2 h-4 w-4" />
            View Module
          </Button>
        </Link>
      </CardHeader>
      {resources.length > 0 && (
        <CardContent className="space-y-3">
          {resources.map((resource) => (
            <div
              key={resource.id}
            className="flex items-center justify-between rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-3 transition hover:border-primary-100 hover:bg-primary-50"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  {getResourceIcon(resource)}
                </span>
                <div>
                  <p className="font-medium text-secondary-900">{resource.title}</p>
                  <p className="text-sm text-secondary-500">
                    {resource.mimeType ?? resource.type ?? 'Resource'} {formatFileSize(resource.fileSize)}
                  </p>
                </div>
              </div>
              {resource.url && (
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-primary-200 px-3 py-1 text-sm text-primary-600 transition hover:bg-primary-100"
                >
                  <Download className="h-4 w-4" />
                  Open
                </a>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  )
}

export function CoursePage() {
  const { courseId } = useParams<{ courseId: string }>()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [moduleFilter, setModuleFilter] = useState('all')
  const [announcementPage, setAnnouncementPage] = useState(1)

  const {
    data: course,
    isLoading: isCourseLoading,
    error: courseError,
  } = useCourse(courseId)
  const {
    data: modules = [],
    isLoading: isModuleLoading,
    error: moduleError,
  } = useCourseModules(courseId)
  const announcementPerPage = 3
  const {
    data: courseAnnouncementsResponse,
    isLoading: isCourseAnnouncementsLoading,
  } = useCourseAnnouncements(courseId, { page: announcementPage, perPage: announcementPerPage, isActive: true })

  const programmeId = course?.programmeId
  const departmentId = course?.departmentId

  const { data: programme, isLoading: isProgrammeLoading } = useQuery({
    queryKey: ['programmes', 'detail', programmeId],
    queryFn: () => programmesService.getProgramme(programmeId!),
    enabled: Boolean(programmeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const { data: department, isLoading: isDepartmentLoading } = useQuery({
    queryKey: ['departments', 'detail', departmentId],
    queryFn: () => departmentsService.getDepartment(departmentId!),
    enabled: Boolean(departmentId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const defaultModuleId = searchParams.get('moduleId')

  useEffect(() => {
    if (!defaultModuleId || modules.length === 0) return
    const element = document.getElementById(`module-${defaultModuleId}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [defaultModuleId, modules])

  useEffect(() => {
    setAnnouncementPage(1)
  }, [courseId])

  const filteredModules = useMemo(() => {
    return modules
      // .filter((module) => {
      //   if (moduleFilter === 'all') return true
      //   return String(module.order ?? module.id) === moduleFilter
      // })
      .filter((module) => {
        if (!searchTerm) return true
        const query = searchTerm.toLowerCase()
        return (
          module.title.toLowerCase().includes(query) ||
          (module.description ?? '').toLowerCase().includes(query) ||
          module.resources.some((resource) =>
            resource.title.toLowerCase().includes(query),
          )
        )
      })
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
  }, [modules, moduleFilter, searchTerm])

  const courseResources = course?.courseLevelResources ?? []
  const courseAnnouncements: AnnouncementModel[] = courseAnnouncementsResponse?.data ?? []
  const courseAnnouncementsMeta = courseAnnouncementsResponse?.meta
  const courseAnnouncementsTotalPages = courseAnnouncementsMeta
    ? Math.max(1, Math.ceil(courseAnnouncementsMeta.total / courseAnnouncementsMeta.perPage))
    : 1

  const statusBadgeMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-200',
    upcoming: 'bg-blue-100 text-blue-700 border-blue-200',
    completed: 'bg-purple-100 text-purple-700 border-purple-200',
    inactive: 'bg-gray-100 text-gray-700 border-gray-200',
  }

  if (!courseId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">
        Invalid course selected.
      </div>
    )
  }

  if (courseError || moduleError) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-secondary-500">
        <BookOpen className="mb-4 h-12 w-12 text-primary-400" />
        <p className="text-lg font-semibold">Unable to load course information</p>
        <p className="text-sm text-secondary-500">
          {(courseError as Error)?.message || (moduleError as Error)?.message || 'Please try again later.'}
        </p>
      </div>
    )
  }

  if (isCourseLoading || isModuleLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">
        Loading course details…
      </div>
    )
  }

  if (!course) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">
        Course not found.
      </div>
    )
  }

  const departmentLabel = departmentId
    ? isDepartmentLoading
      ? 'Loading...'
      : department?.name ?? course.departmentName ?? 'Not assigned'
    : course.departmentName ?? 'Not assigned'

  const programmeLabel = programmeId
    ? isProgrammeLoading
      ? 'Loading...'
      : programme?.title ?? course.programmeTitle ?? 'Not linked'
    : course.programmeTitle ?? 'Not linked'

  const statusClass = statusBadgeMap[course.activityStatus] ?? statusBadgeMap.active
  const firstModuleLink =
    filteredModules.length > 0
      ? `${paths.app.courses.content.build(course.id)}?moduleId=${filteredModules[0].id}`
      : undefined
  const canManage = user?.role === UserRole.ADMIN || user?.role === UserRole.INSTRUCTOR
  const isLearner = user?.role === UserRole.LEARNER
  const manageCourseQuizzesLink = `${paths.app.quizzes.root.absolute}?courseId=${encodeURIComponent(
    course.id ?? '',
  )}&courseTitle=${encodeURIComponent(course.title ?? '')}`
  const viewQuizzesLink = isLearner
    ? paths.app.quizzes.courseList.build(course.id)
    : manageCourseQuizzesLink

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-primary-50 to-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm text-secondary-500">
              <Link to={paths.app.courses.root.absolute} className="inline-flex items-center gap-2 hover:text-secondary-700">
                <ArrowLeft className="h-4 w-4" />
                Back to Courses
              </Link>
              <span className="text-secondary-300">/</span>
              <span className="inline-flex items-center gap-2 text-secondary-600">
                <Layers className="h-4 w-4" />
                Course Overview
              </span>
            </div>
            <div className="flex items-center gap-2">
              {firstModuleLink && (
                <Link to={firstModuleLink}>
                  <Button className="bg-primary-600 hover:bg-primary-700 text-white">
                    <Play className="mr-2 h-4 w-4" />
                    {user?.role === 'learner' ? 'Start Learning' : 'Preview Course'}
                  </Button>
                </Link>
              )}
              <Link to={viewQuizzesLink}>
                <Button variant="outline" className="border-primary-200 text-primary-600 hover:bg-primary-50">
                  View Quizzes
                </Button>
              </Link>
              {canManage && (
                <Link to={paths.app.courses.manage.build(course.id)}>
                  <Button variant="outline" className="border-primary-200 text-primary-600 hover:bg-primary-50">
                    Manage Course
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-3xl font-bold text-secondary-900">{course.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-secondary-500">
                <Badge variant="outline" className={statusClass}>
                  {course.activityStatus.charAt(0).toUpperCase() + course.activityStatus.slice(1)}
                </Badge>
                {course.code && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-secondary-600 shadow-sm">
                    <Layers className="h-4 w-4" />
                    Code: {course.code}
                  </span>
                )}
                {course.credits !== undefined && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-secondary-600 shadow-sm">
                    <GraduationCap className="h-4 w-4" />
                    {course.credits} Credits
                  </span>
                )}
                {(programmeId || course.programmeTitle) && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-secondary-600 shadow-sm">
                    <BookOpen className="h-4 w-4" />
                    {programmeLabel}
                  </span>
                )}
              </div>
            </div>

            <Card className="border-0 bg-white/80 shadow-sm backdrop-blur-sm">
              <CardContent className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="w-full">
                  <Input
                    value={searchTerm}
                    placeholder="Search modules or resources"
                    onChange={(event) => setSearchTerm(event.target.value)}
                  icon={<Search className="h-4 w-4 text-primary-500" />}
                  />
                </div>
                {/* <div className="w-full sm:w-1/3">
                  <Select value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)}>
                    <option value="all">All modules</option>
                    {modules
                      .slice()
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((module) => (
                        <option key={module.id} value={String(module.order ?? module.id)}>
                          Module {module.order ?? '—'} — {module.title}
                        </option>
                      ))}
                  </Select>
                </div> */}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <div className="space-y-4">
              {filteredModules.length === 0 ? (
                <Card className="border border-dashed border-primary-200 bg-white/60 py-12 text-center">
                  <CardContent>
                    <p className="text-lg font-semibold text-secondary-700">No modules found</p>
                    <p className="mt-2 text-sm text-secondary-500">
                      Try adjusting your filters or contact your instructor for updates.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredModules.map((module) => (
                  <ModuleCard
                    key={module.id}
                    module={module}
                    courseId={course.id}
                    isHighlighted={module.id === defaultModuleId}
                  />
                ))
              )}
            </div>

            <div className="space-y-4">
              <Card className="border-0 bg-white shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">Course Summary</CardTitle>
                  <CardDescription>Key information for this course</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-secondary-600">
                  <div className="flex items-center justify-between rounded-xl bg-secondary-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-primary-500" />
                      <div>
                        <p className="font-medium text-secondary-800">Start Date</p>
                        <p>{formatDate(course.startDate)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-secondary-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-primary-500" />
                      <div>
                        <p className="font-medium text-secondary-800">End Date</p>
                        <p>{formatDate(course.endDate)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-secondary-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-primary-500" />
                      <div>
                        <p className="font-medium text-secondary-800">Department</p>
                        <p>{departmentLabel}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-secondary-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-4 w-4 text-primary-500" />
                      <div>
                        <p className="font-medium text-secondary-800">Programme</p>
                        <p>{programmeLabel}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {courseResources.length > 0 && (
                <Card className="border-0 bg-white shadow-md">
                  <CardHeader>
                    <CardTitle className="text-lg">Course Resources</CardTitle>
                    <CardDescription>Additional materials shared for the entire course</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {courseResources.map((resource) => (
                      <div
                        key={resource.id}
                        className="flex items-center justify-between rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-2"
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center flex-shrink-0 rounded-full bg-primary-100 text-primary-600">
                            {getResourceIcon(resource)}
                          </span>
                          <div>
                            <p className="font-medium text-sm text-secondary-800">{resource.title}</p>
                            <p className="text-xs text-secondary-500">{formatFileSize(resource.fileSize)}</p>
                          </div>
                        </div>
                        {resource.url && (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card className="border-0 bg-white shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Megaphone className="h-4 w-4 text-primary-500" />
                    Course Announcements
                  </CardTitle>
                  <CardDescription>Important updates from your instructors</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isCourseAnnouncementsLoading ? (
                    Array.from({ length: 2 }).map((_, index) => (
                      <div key={index} className="animate-pulse space-y-2 rounded-lg border border-secondary-100 p-3">
                        <div className="h-4 w-1/2 rounded bg-secondary-100" />
                        <div className="h-3 w-1/3 rounded bg-secondary-100" />
                        <div className="h-3 rounded bg-secondary-50" />
                      </div>
                    ))
                  ) : courseAnnouncements.length === 0 ? (
                    <p className="text-sm text-secondary-500">No announcements for this course yet.</p>
                  ) : (
                    courseAnnouncements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className="space-y-1 rounded-lg border border-secondary-100 bg-secondary-50/50 p-3"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-secondary-900">{announcement.title}</p>
                          {announcement.isPinned && (
                            <Badge className="bg-warning-100 text-warning-700">Pinned</Badge>
                          )}
                        </div>
                        <p className="text-xs text-secondary-500">
                          {formatDate(announcement.createdAt)} — {announcement.authorName}
                        </p>
                        <p className="text-sm text-secondary-600 line-clamp-2">{announcement.content}</p>
                        <Link
                          to={paths.app.announcements.detail.build(announcement.id)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          View details
                        </Link>
                      </div>
                    ))
                  )}
                </CardContent>
                {courseAnnouncementsMeta && courseAnnouncementsTotalPages > 1 && (
                  <CardContent className="border-t pt-4">
                    <div className="flex items-center justify-between text-xs text-secondary-500">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnnouncementPage(Math.max(1, announcementPage - 1))}
                        disabled={announcementPage <= 1}
                      >
                        Previous
                      </Button>
                      <span>
                        Page {announcementPage} of {courseAnnouncementsTotalPages}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnnouncementPage(Math.min(courseAnnouncementsTotalPages, announcementPage + 1))}
                        disabled={announcementPage >= courseAnnouncementsTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </CardContent>
                )}
                <CardContent className="border-t pt-4">
                  <Link
                    to={paths.app.announcements.root.absolute}
                    className="text-sm font-medium text-primary-600 hover:underline"
                  >
                    View all announcements
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
